import { Brain, Layers, Users, Recycle } from "lucide-react";
import { motion } from "motion/react";
import { useLanguage } from "../context/LanguageContext";
import { useState } from "react";
import { Button } from "./ui/button";

export function WhyChooseUs() {
  const { t } = useLanguage();
  const [showAll, setShowAll] = useState(false);
  
  const features = [
    {
      icon: Brain,
      title: t('whyChoose.technology.title'),
      description: t('whyChoose.technology.description')
    },
    {
      icon: Layers,
      title: t('services.farmDevelopment.title'),
      description: t('services.farmDevelopment.description')
    },
    {
      icon: Users,
      title: t('whyChoose.expert.title'),
      description: t('whyChoose.expert.description')
    },
    {
      icon: Recycle,
      title: t('whyChoose.support.title'),
      description: t('whyChoose.support.description')
    }
  ];

  const visibleFeatures = showAll ? features : features.slice(0, 2);

  return (
    <section className="py-20 px-6 lg:px-20 bg-gradient-to-br from-[#051F20] via-[#0B2B26] to-[#163832] relative overflow-hidden">
      {/* Animated Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        {[...Array(15)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-32 h-32 border border-white rounded-full"
            initial={{ 
              x: Math.random() * 100 + '%',
              y: Math.random() * 100 + '%',
              scale: Math.random() * 0.5 + 0.5
            }}
            animate={{
              y: [null, `${Math.random() * 20 - 10}%`],
              x: [null, `${Math.random() * 20 - 10}%`],
              scale: [null, Math.random() * 0.8 + 0.6]
            }}
            transition={{
              duration: Math.random() * 10 + 10,
              repeat: Infinity,
              repeatType: "reverse",
              ease: "easeInOut"
            }}
          />
        ))}
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Section Header */}
        <div className="text-center mb-20">
          <motion.div 
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="inline-block bg-white/10 backdrop-blur-xl px-6 py-3 rounded-full border border-[#8EB69B]/30 mb-6 shadow-2xl"
          >
            <p className="text-[#8EB69B] tracking-wider uppercase text-sm">Why Choose Us</p>
          </motion.div>
          
          <motion.h2 
            className="text-5xl lg:text-6xl text-white mb-6 leading-tight"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            {t('whyChoose.title')}
          </motion.h2>
          
          <motion.p 
            className="text-white/90 text-lg max-w-3xl mx-auto leading-relaxed"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
          >
            {t('whyChoose.subtitle')}
          </motion.p>
        </div>

        {/* Features Grid with 3D Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {visibleFeatures.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 100, rotateX: -30 }}
              whileInView={{ opacity: 1, y: 0, rotateX: 0 }}
              viewport={{ once: true }}
              transition={{ 
                delay: index * 0.15,
                duration: 0.8,
                type: "spring",
                stiffness: 100
              }}
              whileHover={{ 
                y: -20,
                rotateX: 10,
                rotateY: 5,
                scale: 1.05,
                transition: { duration: 0.3 }
              }}
              style={{ 
                perspective: 1200,
                transformStyle: 'preserve-3d'
              }}
              className="group relative bg-white/10 backdrop-blur-2xl p-10 rounded-3xl border border-white/20 hover:border-[#8EB69B]/50 transition-all duration-500 shadow-2xl"
            >
              {/* 3D Depth Effect */}
              <motion.div
                className="absolute inset-0 bg-gradient-to-br from-[#8EB69B]/20 via-transparent to-[#235347]/20 rounded-3xl"
                style={{ transform: 'translateZ(-10px)' }}
                whileHover={{ opacity: 0.8 }}
              />
              
              {/* Glow Effect */}
              <motion.div
                className="absolute -inset-0.5 bg-gradient-to-r from-[#8EB69B] to-[#235347] rounded-3xl opacity-0 group-hover:opacity-20 blur-xl transition-opacity duration-500"
                animate={{
                  scale: [1, 1.1, 1],
                  opacity: [0, 0.2, 0]
                }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              />
              
              <div className="relative" style={{ transform: 'translateZ(30px)' }}>
                <motion.div 
                  className="h-20 w-20 bg-gradient-to-br from-white via-[#DAF1DE] to-[#8EB69B] rounded-2xl flex items-center justify-center mb-6 shadow-2xl relative overflow-hidden"
                  whileHover={{ 
                    rotate: 360,
                    scale: 1.1
                  }}
                  transition={{ duration: 0.8, type: "spring" }}
                >
                  {/* Inner glow */}
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-tr from-white/40 to-transparent"
                    animate={{
                      rotate: 360
                    }}
                    transition={{
                      duration: 4,
                      repeat: Infinity,
                      ease: "linear"
                    }}
                  />
                  <feature.icon className="h-10 w-10 text-[#051F20] relative z-10" />
                </motion.div>
                
                <motion.h3 
                  className="text-white text-2xl mb-4 group-hover:text-[#8EB69B] transition-colors"
                  whileHover={{ x: 5 }}
                >
                  {feature.title}
                </motion.h3>
                
                <p className="text-white/80 leading-relaxed">
                  {feature.description}
                </p>
              </div>
              
              {/* Corner Accents */}
              <div className="absolute top-4 right-4 w-16 h-16 border-t-2 border-r-2 border-[#8EB69B]/20 group-hover:border-[#8EB69B]/60 rounded-tr-3xl transition-colors duration-300"></div>
              <div className="absolute bottom-4 left-4 w-16 h-16 border-b-2 border-l-2 border-[#8EB69B]/20 group-hover:border-[#8EB69B]/60 rounded-bl-3xl transition-colors duration-300"></div>
            </motion.div>
          ))}
        </div>

        {/* View More/Less Button */}
        <motion.div 
          className="text-center mt-12"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
        >
          <motion.div
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Button 
              onClick={() => setShowAll(!showAll)}
              className="bg-white/10 hover:bg-white/20 text-white border-2 border-white/20 px-8 py-6 rounded-xl backdrop-blur-xl"
            >
              {showAll ? t('common.readMore') : t('common.viewAll')}
            </Button>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}